import React, {useMemo, useEffect, useRef} from 'react';
import {Modal, View, Text, TouchableOpacity, Platform} from 'react-native';
import {SafeAreaView} from 'react-native-safe-area-context';
import {useTranslation} from 'react-i18next';
import {ConfirmModalProps} from './ConfirmModalProps';
import {useScreenTheme} from '@shared/hooks/useScreenTheme';
import CloseIcon from '@shared/assets/icons/CloseIcon';
import {Button} from '@shared/components/ui';
import {buttonVariants} from '@shared/constants/global';
import {createConfirmModalStyles} from './ConfirmModal.styles';

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isVisible,
  onClose,
  onConfirm,
  selectedActivity,
  onChangeSelection,
  title,
  closeCTAAccessibilityHint,
  errorMessage,
  description,
  descTwo,
  selectedValueTitle,
  additionalCTA,
  onAdditionalAction,
  additionalCTAButton,
  additionalCTAHint,
  changeCTA,
  confirmCTA,
  confirmCTAButton,
  confirmCTAHint,
  changeSelectionButton,
  changeSelectionHint,
  customHeight,
  size,
  titleIcon,
  showClose = true,
  customWidth,
}) => {
  const theme = useScreenTheme('RepairDetailsScreen');
  const styles = useMemo(() => createConfirmModalStyles(theme), [theme]);
  const {t} = useTranslation();

  // ── Web accessibility: focus management ──────────────────────────────────
  // Ref for the SafeAreaView dialog container — primary focus target on open.
  const modalContainerRef = useRef<any>(null);
  // Ref for the title Text — fallback focus target when container ref misses.
  const modalTitleRef = useRef<any>(null);
  // Tracks the element that had focus before the modal opened so it can be
  // restored when the modal closes (web only).
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  // Move focus into the modal whenever it becomes visible (web only).
  useEffect(() => {
    if (Platform.OS !== 'web' || !isVisible) {return;}

    // Save currently-focused element so it can be restored on close.
    previouslyFocusedRef.current =
      typeof document !== 'undefined'
        ? (document.activeElement as HTMLElement)
        : null;

    // 300 ms lets the fade-in animation complete before shifting focus,
    // preventing a race condition where the DOM node is not yet ready.
    const timer = setTimeout(() => {
      if (modalContainerRef.current) {
        const el = modalContainerRef.current as HTMLElement;
        if (!el.hasAttribute('tabindex')) {
          el.setAttribute('tabindex', '-1');
        }
        el.focus({preventScroll: true});
      } else if (modalTitleRef.current) {
        const el = modalTitleRef.current as HTMLElement;
        // tabIndex="-1" makes the title programmatically focusable without
        // inserting it into the natural Tab order. Static text should not be
        // a Tab stop; the dialog container's aria-label already announces the
        // title to screen readers when the container is focused.
        if (!el.hasAttribute('tabindex')) {
          el.setAttribute('tabindex', '-1');
        }
        el.focus({preventScroll: true});
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [isVisible]);

  // Install Tab/Shift+Tab focus trap so keyboard users cannot leave the dialog.
  // aria-modal="true" handles screen-reader background exclusion.
  // We do NOT set the `inert` attribute on body siblings because the RN Web
  // modal portal is appended asynchronously — any timing mistake marks the
  // modal itself inert and freezes the entire application.
  useEffect(() => {
    if (Platform.OS !== 'web' || !isVisible) {return;}

    const focusableSelectors = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');

    const trapHandler = (e: Event) => {
      const ke = e as KeyboardEvent;
      if (ke.key !== 'Tab' || !modalContainerRef.current) {return;}
      const focusable = Array.from(
        (modalContainerRef.current as HTMLElement).querySelectorAll<HTMLElement>(
          focusableSelectors,
        ),
      ).filter(el => !el.closest('[aria-hidden="true"]'));
      if (focusable.length === 0) {return;}
      const first = focusable[0] as HTMLElement;
      const last = focusable[focusable.length - 1] as HTMLElement;
      if (ke.shiftKey) {
        if (document.activeElement === first) {
          ke.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          ke.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', trapHandler);

    return () => {
      document.removeEventListener('keydown', trapHandler);
      // Restore focus to the element that was active before the modal opened.
      if (
        previouslyFocusedRef.current &&
        typeof previouslyFocusedRef.current.focus === 'function'
      ) {
        previouslyFocusedRef.current.focus({preventScroll: true} as any);
        previouslyFocusedRef.current = null;
      }
    };
  }, [isVisible]);
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <Modal
      visible={isVisible}
      transparent
      animationType="fade"
      accessibilityViewIsModal={true}
      supportedOrientations={[
        'portrait',
        'landscape',
        'landscape-left',
        'landscape-right',
      ]}>
      <View style={styles.container}>
        <SafeAreaView
          ref={modalContainerRef}
          style={[styles.modal, customHeight, customWidth]}
          // @ts-ignore — web-only ARIA role for modal dialog
          role="dialog"
          aria-modal={true}
          aria-label={title}>
          <View style={styles.header}>
            <View style={styles.titleContainer}>
              {titleIcon && (
                <View style={styles.iconContainer}>{titleIcon}</View>
              )}
              <Text
                ref={modalTitleRef}
                style={styles.title}
                accessible={true}
                accessibilityRole="header"
                accessibilityLabel={title}>
                {title}
              </Text>
            </View>
            {showClose && (
              <TouchableOpacity
                onPress={onClose}
                testID="closeButton"
                accessible={true}
                accessibilityRole="button"
                accessibilityLabel={t('common.accessibility.modalClose')}
                accessibilityHint={closeCTAAccessibilityHint}
                style={styles.closeButton}>
                <CloseIcon color={theme.closeModalIconColor} />
              </TouchableOpacity>
            )}
          </View>

          <View style={styles.content}>
            {errorMessage ? (
              <Text
                style={styles.errorMessageText}
                accessible={true}
                accessibilityLabel={errorMessage}>
                {errorMessage}
              </Text>
            ) : null}
            {description && (
              <Text
                style={styles.confirmationText}
                accessible={true}
                accessibilityLabel={description}>
                {description}
              </Text>
            )}
            {descTwo && (
              <Text
                style={[styles.confirmationText, styles.confirmationTextTwo]}
                accessible={true}
                accessibilityLabel={descTwo}>
                {descTwo}
              </Text>
            )}
            {selectedActivity && (
              <View
                style={styles.selectedActivityContainer}
                accessible={true}
                accessibilityLabel={`${selectedValueTitle}: ${selectedActivity}`}>
                <Text
                  style={styles.label}
                  accessible={false}>
                  {selectedValueTitle}
                </Text>
                <Text
                  style={styles.selectedActivityText}
                  accessible={false}>
                  {selectedActivity}
                </Text>
              </View>
            )}
          </View>

          <View style={styles.footerButtons}>
            {/* Optional far-left button — spacer always present to push rightContainer to the right */}

            {additionalCTA && (
              <Button
                title={additionalCTA}
                variant={buttonVariants.secondary}
                onPress={onAdditionalAction!}
                fullWidth={false}
                size={size}
                containerStyle={styles.changeSelectionButton}
                customTextStyle={styles.changeSelectionButtonText}
                accessibilityRole="button"
                accessibilityLabel={additionalCTAButton}
                accessibilityHint={additionalCTAHint}
              />
            )}

            <View style={styles.footerSpacer} />

            <View style={styles.rightContainer}>
              {changeCTA && (
                <Button
                  title={changeCTA}
                  variant={buttonVariants.secondary}
                  onPress={onChangeSelection!}
                  fullWidth={false}
                  size={size}
                  containerStyle={styles.changeSelectionButton}
                  customTextStyle={styles.changeSelectionButtonText}
                  accessibilityRole="button"
                  accessibilityLabel={changeSelectionButton}
                  accessibilityHint={changeSelectionHint}
                />
              )}
              {confirmCTA && (
                <Button
                  title={confirmCTA}
                  variant={buttonVariants.primary}
                  onPress={onConfirm}
                  fullWidth={false}
                  size={size}
                  containerStyle={styles.confirmButton}
                  customTextStyle={styles.confirmButtonText}
                  accessibilityRole="button"
                  accessibilityLabel={confirmCTAButton}
                  accessibilityHint={confirmCTAHint}
                />
              )}
            </View>
          </View>
        </SafeAreaView>
      </View>
    </Modal>
  );
};

export default ConfirmModal;
